import type { NodeParameters } from '../../../types/node-properties';

/**
 * Validation function for MS SQL Server node parameters
 * Returns array of error messages, or empty array if valid
 */
export function validate(parameters: NodeParameters): string[] {
  const errors: string[] = [];

  // Validate operation
  if (!parameters.operation) {
    errors.push('Operation is required');
    return errors;
  }

  const operation = parameters.operation as string;

  // Validate Execute Query operation
  if (operation === 'executeQuery') {
    const queryMode = parameters.queryMode as string;
    
    if (queryMode === 'single') {
      if (!parameters.query || (parameters.query as string).trim() === '') {
        errors.push('Query is required for Execute Query operation');
      }
    } else if (queryMode === 'multiple') {
      const queries = parameters.queries as any;
      if (!queries || !queries.query || queries.query.length === 0) {
        errors.push('At least one query is required for Multiple Queries mode');
      } else {
        // Validate each query
        queries.query.forEach((q: any, index: number) => {
          if (!q.sql || q.sql.trim() === '') {
            errors.push(`Query ${index + 1} cannot be empty`);
          }
        });
      }
    }

    // Validate max rows
    if (parameters.maxRows !== undefined && parameters.maxRows !== null) {
      const maxRows = parameters.maxRows as number;
      if (maxRows < 0) {
        errors.push('Max rows must be 0 (unlimited) or a positive number');
      }
    }
  }

  // Validate Insert/Update/Delete operations
  if (['insert', 'update', 'delete'].includes(operation)) {
    // Validate table name
    if (!parameters.table || (parameters.table as string).trim() === '') {
      errors.push('Table name is required');
    }

    // Validate table name format (basic SQL identifier validation)
    if (parameters.table) {
      const tableName = parameters.table as string;
      // Allow alphanumeric, underscore, dot (for schema.table), and brackets (for [table])
      if (!/^[\w.\[\]]+$/.test(tableName)) {
        errors.push('Table name contains invalid characters');
      }
    }
  }

  // Validate Insert/Update column input
  if (['insert', 'update'].includes(operation)) {
    const columnInputMethod = parameters.columnInputMethod as string;

    if (columnInputMethod === 'collection') {
      const columnMapping = parameters.columnMapping as any;
      if (!columnMapping || !columnMapping.mapping || columnMapping.mapping.length === 0) {
        errors.push('At least one column mapping is required when using "Define Below" method');
      } else {
        // Validate each mapping
        columnMapping.mapping.forEach((mapping: any, index: number) => {
          if (!mapping.column || mapping.column.trim() === '') {
            errors.push(`Column name is required for mapping ${index + 1}`);
          }
        });
      }
    } else if (columnInputMethod === 'json') {
      if (!parameters.columnsJson || (parameters.columnsJson as string).trim() === '') {
        errors.push('Columns JSON is required when using "Using JSON" method');
      } else {
        // Validate JSON format
        try {
          const parsed = JSON.parse(parameters.columnsJson as string);
          if (typeof parsed !== 'object' || Array.isArray(parsed)) {
            errors.push('Columns JSON must be an object (not an array)');
          } else if (Object.keys(parsed).length === 0) {
            errors.push('Columns JSON must contain at least one column');
          }
        } catch (e) {
          errors.push('Columns JSON is not valid JSON');
        }
      }
    }
  }

  // Validate Update operation
  if (operation === 'update') {
    if (!parameters.updateKey || (parameters.updateKey as string).trim() === '') {
      errors.push('Update key is required for Update operation');
    }
  }

  // Validate Delete operation
  if (operation === 'delete') {
    if (!parameters.deleteKey || (parameters.deleteKey as string).trim() === '') {
      errors.push('Delete key is required for Delete operation');
    }
  }

  // Validate batch size
  if (parameters.batchSize !== undefined && parameters.batchSize !== null) {
    const batchSize = parameters.batchSize as number;
    if (batchSize < 1) {
      errors.push('Batch size must be at least 1');
    }
    if (batchSize > 10000) {
      errors.push('Batch size cannot exceed 10000');
    }
  }

  // Validate query timeout
  if (parameters.queryTimeout !== undefined && parameters.queryTimeout !== null) {
    const queryTimeout = parameters.queryTimeout as number;
    if (queryTimeout < 1) {
      errors.push('Query timeout must be at least 1 second');
    }
  }

  // Validate return columns format (if provided)
  if (operation === 'insert' && parameters.returnColumns) {
    const returnColumns = parameters.returnColumns as string;
    if (returnColumns.trim() !== '') {
      // Basic validation - check for SQL injection attempts
      if (/[;'"\\]/.test(returnColumns)) {
        errors.push('Return columns contains invalid characters');
      }
    }
  }

  return errors;
}

export default validate;
